import React, { useEffect, useState, useCallback } from "react";
import Header from "../master/SideBar";
import Nav from "../master/NavBar";
import {
  SweetAlertShow,
  ToastMain,
  LoadingDiv,
  formatTime,
} from "../master/Master";
import { StaffList } from "../../api/Staff";
import { imgBaseUrl } from "../../api/api";
import Swal from "sweetalert2";
import StaffLayout from "./StaffLayout";

// Add to Staff.js api file:
// export const salaryPayDetails = (addUrl = "") =>
//   api.get(`staff/v2/salary/pay/details/v2?${addUrl}`);
// export const staffSalaryPay = (data) =>
//   api.post(`staff/v2/salary/pay/v2`, data);
import { salaryPayDetails, staffSalaryPay } from "../../api/Staff";

// ─── Helpers ────────────────────────────────────────────────────────────────
const rupee = (n) =>
  "₹" +
  parseFloat(n || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const statusBadge = (status) => {
  const map = {
    Paid: "bg-label-success",
    Partial: "bg-label-warning",
    Due: "bg-label-danger",
  };
  return (
    <span
      className={`badge ${map[status] || "bg-label-secondary"} rounded-pill px-3`}
    >
      {status}
    </span>
  );
};

const initials = (name) =>
  (name || "")
    .split(" ")
    .map((x) => x[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

// ────────────────────────────────────────────────────────────────────────────
export default function StaffSalaryPay() {
  const [loading, setLoading] = useState(false);
  const [staffSearch, setStaffSearch] = useState("");
  const [staffList, setStaffList] = useState([]);
  const [selectedStaff, setSelectedStaff] = useState(null);

  // Salary data from API
  const [dueSalaries, setDueSalaries] = useState([]);
  const [paidHistory, setPaidHistory] = useState([]);
  const [currentAdvance, setCurrentAdvance] = useState(null);
  const [summary, setSummary] = useState(null);
  const [banks, setBanks] = useState([]);

  // UI state
  const [paymentType, setPaymentType] = useState("normal");
  const [checkedIds, setCheckedIds] = useState([]);
  const [showPaidHistory, setShowPaidHistory] = useState(false);
  const [useCustomAmount, setUseCustomAmount] = useState(false);
  const [customAmount, setCustomAmount] = useState("");

  const [payForm, setPayForm] = useState({
    debit_account: "",
    payment_date: new Date().toISOString().split("T")[0],
    remark: "",
    advance_amount: "",
  });

  // ── Staff list ─────────────────────────────────────────────────────────────
  const fetchStaffs = useCallback(async () => {
    try {
      const res = await StaffList(`&search=${staffSearch}&status=1`);
      if (res?.data?.status) setStaffList(res.data.data || []);
    } catch (_) {}
  }, [staffSearch]);

  useEffect(() => {
    fetchStaffs();
  }, [fetchStaffs]);

  // ── Salary details ─────────────────────────────────────────────────────────
  const fetchSalaryDetails = async (staffId) => {
    setLoading(true);
    setDueSalaries([]);
    setPaidHistory([]);
    setCurrentAdvance(null);
    setSummary(null);
    setCheckedIds([]);
    setUseCustomAmount(false);
    setCustomAmount("");
    try {
      const res = await salaryPayDetails(`staff_id=${staffId}`);
      if (res?.data?.status) {
        const due = res.data.due_salaries || [];
        setDueSalaries(due);
        setPaidHistory(res.data.paid_history || []);
        setCurrentAdvance(res.data.current_advance || null);
        setSummary(res.data.summary || null);
        setBanks(res.data.banks || []);
        setCheckedIds(due.map((s) => s.salary_id));
        setPayForm((p) => ({
          ...p,
          debit_account: res.data.banks?.[0]?.account_no || "",
        }));
      } else {
        SweetAlertShow(
          "info",
          "Notice",
          res?.data?.message || "No salary data found.",
        );
      }
    } catch (err) {
      SweetAlertShow(
        "error",
        "Error",
        err?.response?.data?.message || err?.message,
      );
    } finally {
      setLoading(false);
    }
  };

  const handleSelectStaff = (staff) => {
    setSelectedStaff(staff);
    setPaymentType("normal");
    fetchSalaryDetails(staff.id);
  };

  // ── Checkboxes ─────────────────────────────────────────────────────────────
  const toggleCheck = (id) =>
    setCheckedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );

  const toggleAll = () =>
    setCheckedIds(
      checkedIds.length === dueSalaries.length
        ? []
        : dueSalaries.map((s) => s.salary_id),
    );

  // ── Payment amounts ────────────────────────────────────────────────────────
  const selectedRows = dueSalaries.filter((s) =>
    checkedIds.includes(s.salary_id),
  );
  const totalSelectedPending = selectedRows.reduce(
    (acc, s) => acc + s.pending_amount,
    0,
  );
  const normalPayAmount = useCustomAmount
    ? parseFloat(customAmount) || 0
    : totalSelectedPending;
  const advancePayAmount = parseFloat(payForm.advance_amount) || 0;
  const currentMonthLabel = new Date().toLocaleString("default", {
    month: "long",
    year: "numeric",
  });

  // ── Pay submit ─────────────────────────────────────────────────────────────
  const handlePay = async (e) => {
    e.preventDefault();
    if (!selectedStaff)
      return SweetAlertShow("info", "Info", "Select a staff member.");
    if (!payForm.debit_account)
      return SweetAlertShow("info", "Info", "Select a bank account.");

    const isAdvance = paymentType === "advance";
    const amount = isAdvance ? advancePayAmount : normalPayAmount;

    if (amount <= 0)
      return SweetAlertShow("info", "Info", "Enter a valid amount.");
    if (!isAdvance && checkedIds.length === 0)
      return SweetAlertShow(
        "info",
        "Info",
        "Select at least one month to pay.",
      );

    const html = isAdvance
      ? `Pay advance <b>${rupee(amount)}</b> for <b>${currentMonthLabel}</b> to <b>${selectedStaff.name}</b>?`
      : `Pay <b>${rupee(amount)}</b> for <b>${selectedRows.map((s) => s.month_label).join(", ")}</b> to <b>${selectedStaff.name}</b>?`;

    const ok = await Swal.fire({
      title: "Confirm Payment",
      html,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Yes, Pay Now",
      cancelButtonText: "Cancel",
    });
    if (!ok?.isConfirmed) return;

    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("staff_id", selectedStaff.id);
      fd.append("paid_amount", amount);
      fd.append("debit_account", payForm.debit_account);
      fd.append("payment_date", payForm.payment_date);
      fd.append("remark", payForm.remark);
      fd.append("payment_type", paymentType);
      if (!isAdvance) fd.append("salary_ids", JSON.stringify(checkedIds));

      const res = await staffSalaryPay(fd);
      if (res?.data?.status) {
        ToastMain.fire({ icon: "success", title: res.data.message });
        fetchSalaryDetails(selectedStaff.id);
        setPayForm((p) => ({ ...p, remark: "", advance_amount: "" }));
        setCustomAmount("");
        setUseCustomAmount(false);
      } else {
        SweetAlertShow("error", "Failed", res?.data?.message);
      }
    } catch (err) {
      SweetAlertShow(
        "error",
        "Error",
        err?.response?.data?.message || err?.message,
      );
    } finally {
      setLoading(false);
    }
  };

  // ── Bank dropdown helper ───────────────────────────────────────────────────
  const BankSelect = () => (
    <div className="form-floating form-floating-outline">
      <select
        className="form-select"
        value={payForm.debit_account}
        onChange={(e) =>
          setPayForm((p) => ({ ...p, debit_account: e.target.value }))
        }
        required
      >
        <option value="" disabled>
          Select Bank Account
        </option>
        {banks.map((b) => (
          <option key={b.account_no} value={b.account_no}>
            A/C: {b.account_no} (Bal: ₹
            {parseFloat(b.amount).toLocaleString("en-IN")})
          </option>
        ))}
      </select>
      <label>Debit Bank Account</label>
    </div>
  );

  // ────────────────────────────────────────────────────────────────────────────
  return (
    <>
      {loading && <LoadingDiv />}
      <div className="layout-wrapper layout-content-navbar">
        <div className="layout-container">
          <Header />
          <div className="layout-page">
            <Nav />
            <div className="content-wrapper">
              <div className="container-fluid mt-2">
                <div className="row">
                  <StaffLayout />

                  {/* Page title */}
                  <div className="d-flex align-items-center justify-content-between mb-4">
                    <div>
                      <h3 className="fw-bold text-primary mb-1">
                        <i className="ri-money-rupee-circle-line me-2"></i>Staff
                        Salary Payment
                      </h3>
                      <p className="text-muted mb-0">
                        Review pending salaries and record payments month-wise.
                      </p>
                    </div>
                  </div>

                  <div className="row g-4">
                    {/* ══ LEFT: Staff list ══════════════════════════════════════ */}
                    <div className="col-xl-3 col-lg-4">
                      <div className="card border-0 shadow-sm">
                        <div className="card-header bg-label-primary fw-semibold border-0">
                          <i className="ri-team-line me-2"></i>Select Staff
                        </div>
                        <div className="card-body p-3">
                          <div className="form-floating form-floating-outline mb-3">
                            <input
                              type="text"
                              className="form-control"
                              placeholder="Search"
                              value={staffSearch}
                              onChange={(e) => setStaffSearch(e.target.value)}
                            />
                            <label>Search Name / Mobile / Reg.</label>
                          </div>
                          <div style={{ maxHeight: "65vh", overflowY: "auto" }}>
                            {staffList.length === 0 && (
                              <div className="text-center text-muted py-4">
                                <i className="ri-user-search-line fs-1 d-block mb-1"></i>
                                No staff found
                              </div>
                            )}
                            {staffList.map((staff) => (
                              <div
                                key={staff.id}
                                className={`d-flex align-items-center gap-2 p-2 rounded-3 mb-1 border ${
                                  selectedStaff?.id === staff.id
                                    ? "border-primary bg-label-primary"
                                    : "border-transparent bg-light"
                                }`}
                                style={{ cursor: "pointer" }}
                                onClick={() => handleSelectStaff(staff)}
                              >
                                {staff?.img ? (
                                  <img
                                    src={`${imgBaseUrl}${staff.img}`}
                                    alt={staff.name}
                                    className="rounded-circle border flex-shrink-0"
                                    style={{
                                      width: 38,
                                      height: 38,
                                      objectFit: "cover",
                                    }}
                                  />
                                ) : (
                                  <div
                                    className="rounded-circle bg-label-secondary d-flex align-items-center justify-content-center fw-bold text-primary flex-shrink-0"
                                    style={{
                                      width: 38,
                                      height: 38,
                                      fontSize: 13,
                                    }}
                                  >
                                    {initials(staff.name)}
                                  </div>
                                )}
                                <div className="overflow-hidden flex-grow-1">
                                  <div className="fw-semibold text-truncate small">
                                    {staff.name}
                                  </div>
                                  <div
                                    className="text-muted"
                                    style={{ fontSize: 11 }}
                                  >
                                    {staff.staff_type}
                                  </div>
                                </div>
                                {selectedStaff?.id === staff.id && (
                                  <i className="ri-checkbox-circle-fill text-primary"></i>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* ══ RIGHT: Salary details ═════════════════════════════════ */}
                    <div className="col-xl-9 col-lg-8">
                      {!selectedStaff ? (
                        <div className="card border-0 shadow-sm">
                          <div className="card-body text-center py-5">
                            <div
                              className="rounded-circle bg-label-secondary d-inline-flex align-items-center justify-content-center mb-3"
                              style={{ width: 80, height: 80 }}
                            >
                              <i
                                className="ri-hand-coin-line"
                                style={{ fontSize: 34 }}
                              ></i>
                            </div>
                            <h5 className="mb-1">No Staff Selected</h5>
                            <p className="text-muted mb-0">
                              Pick a staff member to view and pay salary.
                            </p>
                          </div>
                        </div>
                      ) : (
                        <>
                          {/* Staff + Summary strip */}
                          <div className="card border-0 shadow-sm mb-3">
                            <div className="card-body py-3">
                              <div className="d-flex align-items-center gap-3 flex-wrap">
                                {selectedStaff?.img ? (
                                  <img
                                    src={`${imgBaseUrl}${selectedStaff.img}`}
                                    alt={selectedStaff.name}
                                    className="rounded-circle border"
                                    style={{
                                      width: 48,
                                      height: 48,
                                      objectFit: "cover",
                                    }}
                                  />
                                ) : (
                                  <div
                                    className="rounded-circle bg-label-primary d-flex align-items-center justify-content-center fw-bold text-primary"
                                    style={{
                                      width: 48,
                                      height: 48,
                                      fontSize: 16,
                                    }}
                                  >
                                    {initials(selectedStaff.name)}
                                  </div>
                                )}
                                <div>
                                  <h5 className="mb-0 fw-bold">
                                    {selectedStaff.name}
                                  </h5>
                                  <small className="text-muted">
                                    {selectedStaff.staff_type} · Reg:{" "}
                                    {selectedStaff.registration_no} ·{" "}
                                    <i className="ri-phone-line me-1"></i>
                                    {selectedStaff.mobile}
                                  </small>
                                </div>
                                {summary && (
                                  <div className="ms-auto d-flex gap-2 flex-wrap">
                                    <div className="text-center border rounded-3 px-3 py-2">
                                      <div
                                        className="text-muted"
                                        style={{ fontSize: 11 }}
                                      >
                                        Pending Months
                                      </div>
                                      <div className="fw-bold text-danger">
                                        {summary.total_pending_months}
                                      </div>
                                    </div>
                                    <div className="text-center border rounded-3 px-3 py-2">
                                      <div
                                        className="text-muted"
                                        style={{ fontSize: 11 }}
                                      >
                                        Total Pending
                                      </div>
                                      <div className="fw-bold text-danger">
                                        {rupee(summary.total_pending_amount)}
                                      </div>
                                    </div>
                                    <div className="text-center border rounded-3 px-3 py-2">
                                      <div
                                        className="text-muted"
                                        style={{ fontSize: 11 }}
                                      >
                                        Total Paid
                                      </div>
                                      <div className="fw-bold text-success">
                                        {rupee(summary.total_paid_amount)}
                                      </div>
                                    </div>
                                    {paidHistory.length > 0 && (
                                      <div className="text-center border rounded-3 px-3 py-2">
                                        <div
                                          className="text-muted"
                                          style={{ fontSize: 11 }}
                                        >
                                          Paid Months
                                        </div>
                                        <div className="fw-bold text-success">
                                          {summary.total_paid_months}
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>

                          {/* Advance info banner */}
                          {currentAdvance && (
                            <div className="alert alert-info d-flex align-items-center gap-2 mb-3">
                              <i className="ri-information-line fs-5"></i>
                              <div>
                                <strong>Advance Given:</strong> ₹
                                {parseFloat(
                                  currentAdvance.advance_given,
                                ).toLocaleString("en-IN")}{" "}
                                has already been paid as advance for{" "}
                                <strong>{currentAdvance.month_label}</strong>.
                                This will be deducted when the month's salary is
                                generated on 1st of next month.
                              </div>
                            </div>
                          )}

                          {/* Tabs */}
                          <div className="card border-0 shadow-sm mb-3">
                            <div className="card-header border-0 bg-transparent pb-0">
                              <ul className="nav nav-tabs card-header-tabs">
                                <li className="nav-item">
                                  <button
                                    className={`nav-link ${paymentType === "normal" ? "active fw-semibold" : ""}`}
                                    onClick={() => setPaymentType("normal")}
                                  >
                                    <i className="ri-calendar-check-line me-2"></i>
                                    Pay Salary
                                    {dueSalaries.length > 0 && (
                                      <span className="badge bg-danger ms-2 rounded-pill">
                                        {dueSalaries.length}
                                      </span>
                                    )}
                                  </button>
                                </li>
                                <li className="nav-item">
                                  <button
                                    className={`nav-link ${paymentType === "advance" ? "active fw-semibold" : ""}`}
                                    onClick={() => setPaymentType("advance")}
                                  >
                                    <i className="ri-send-plane-line me-2"></i>
                                    Advance Salary
                                    <span
                                      className="badge bg-label-warning ms-2 rounded-pill"
                                      style={{ fontSize: 10 }}
                                    >
                                      {currentMonthLabel}
                                    </span>
                                  </button>
                                </li>
                              </ul>
                            </div>

                            {/* ── NORMAL TAB ─────────────────────────────────── */}
                            {paymentType === "normal" && (
                              <div className="card-body">
                                {dueSalaries.length === 0 ? (
                                  <div className="text-center py-5 text-muted">
                                    <i className="ri-checkbox-circle-line text-success fs-1 d-block mb-2"></i>
                                    <h6>All salaries are paid!</h6>
                                    <p className="mb-0 small">
                                      No pending months found for{" "}
                                      {selectedStaff.name}.
                                    </p>
                                  </div>
                                ) : (
                                  <>
                                    {/* Header row */}
                                    <div className="d-flex align-items-center justify-content-between mb-3">
                                      <h6 className="fw-bold mb-0">
                                        <i className="ri-list-check me-2 text-danger"></i>
                                        Pending / Due Months (
                                        {dueSalaries.length})
                                      </h6>
                                      <button
                                        className="btn btn-sm btn-outline-primary"
                                        onClick={toggleAll}
                                      >
                                        {checkedIds.length ===
                                        dueSalaries.length
                                          ? "Deselect All"
                                          : "Select All"}
                                      </button>
                                    </div>

                                    {/* Pending months table */}
                                    <div className="table-responsive mb-4">
                                      <table className="table align-middle table-hover mb-0">
                                        <thead className="table-light">
                                          <tr>
                                            <th width="44">
                                              <input
                                                type="checkbox"
                                                className="form-check-input"
                                                checked={
                                                  checkedIds.length ===
                                                    dueSalaries.length &&
                                                  dueSalaries.length > 0
                                                }
                                                onChange={toggleAll}
                                              />
                                            </th>
                                            <th>Month</th>
                                            <th className="text-center">
                                              Working
                                            </th>
                                            <th className="text-center">
                                              Present
                                            </th>
                                            <th className="text-center">
                                              Half
                                            </th>
                                            <th className="text-center">
                                              Absent
                                            </th>
                                            <th className="text-center">
                                              Leave
                                            </th>
                                            <th className="text-center">
                                              OT Hrs
                                            </th>
                                            <th className="text-end">
                                              Base Salary
                                            </th>
                                            <th className="text-end">Earned</th>
                                            <th className="text-end">
                                              OT Bonus
                                            </th>
                                            <th className="text-end">
                                              Net Salary
                                            </th>
                                            <th className="text-end">Paid</th>
                                            <th className="text-end text-danger">
                                              Pending
                                            </th>
                                            <th className="text-center">
                                              Status
                                            </th>
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {dueSalaries.map((salary) => {
                                            const isChecked =
                                              checkedIds.includes(
                                                salary.salary_id,
                                              );
                                            return (
                                              <tr
                                                key={salary.salary_id}
                                                className={
                                                  isChecked
                                                    ? "table-primary"
                                                    : ""
                                                }
                                                style={{ cursor: "pointer" }}
                                                onClick={() =>
                                                  toggleCheck(salary.salary_id)
                                                }
                                              >
                                                <td
                                                  onClick={(e) =>
                                                    e.stopPropagation()
                                                  }
                                                >
                                                  <input
                                                    type="checkbox"
                                                    className="form-check-input"
                                                    checked={isChecked}
                                                    onChange={() =>
                                                      toggleCheck(
                                                        salary.salary_id,
                                                      )
                                                    }
                                                  />
                                                </td>
                                                <td className="fw-semibold">
                                                  {salary.month_label}
                                                </td>
                                                <td className="text-center">
                                                  {salary.total_working_days}
                                                </td>
                                                <td className="text-center">
                                                  <span
                                                    className={
                                                      salary.present_days > 0
                                                        ? "text-success fw-semibold"
                                                        : "text-muted"
                                                    }
                                                  >
                                                    {salary.attendance_tracked ? (
                                                      salary.present_days
                                                    ) : (
                                                      <span className="text-muted small">
                                                        Fixed
                                                      </span>
                                                    )}
                                                  </span>
                                                </td>
                                                <td className="text-center">
                                                  {salary.half_days > 0 ? (
                                                    <span className="text-warning fw-semibold">
                                                      {salary.half_days}
                                                    </span>
                                                  ) : (
                                                    <span className="text-muted">
                                                      —
                                                    </span>
                                                  )}
                                                </td>
                                                <td className="text-center">
                                                  {salary.absent_days > 0 ? (
                                                    <span className="text-danger">
                                                      {salary.absent_days}
                                                    </span>
                                                  ) : (
                                                    <span className="text-muted">
                                                      —
                                                    </span>
                                                  )}
                                                </td>
                                                <td className="text-center">
                                                  <span>
                                                    {salary.leave_days}
                                                  </span>
                                                  {salary.unpaid_leave_days >
                                                    0 && (
                                                    <span className="text-danger ms-1 small">
                                                      (-
                                                      {salary.unpaid_leave_days}
                                                      )
                                                    </span>
                                                  )}
                                                </td>
                                                <td className="text-center">
                                                  {salary.overtime_hours > 0 ? (
                                                    <span className="text-primary fw-semibold">
                                                      {salary.overtime_hours}h
                                                    </span>
                                                  ) : (
                                                    <span className="text-muted">
                                                      —
                                                    </span>
                                                  )}
                                                </td>
                                                <td className="text-end">
                                                  {rupee(salary.base_salary)}
                                                </td>
                                                <td className="text-end">
                                                  {rupee(salary.earned_salary)}
                                                </td>
                                                <td className="text-end text-primary">
                                                  {rupee(salary.overtime_bonus)}
                                                </td>
                                                <td className="text-end fw-bold">
                                                  {rupee(salary.net_salary)}
                                                </td>
                                                <td className="text-end text-success">
                                                  {rupee(salary.paid_amount)}
                                                </td>
                                                <td className="text-end fw-bold text-danger">
                                                  {rupee(salary.pending_amount)}
                                                </td>
                                                <td className="text-center">
                                                  {statusBadge(
                                                    salary.payment_status,
                                                  )}
                                                </td>
                                              </tr>
                                            );
                                          })}
                                        </tbody>
                                        {/* Footer: selected total */}
                                        {checkedIds.length > 0 && (
                                          <tfoot className="table-light fw-bold">
                                            <tr>
                                              <td
                                                colSpan={13}
                                                className="text-end"
                                              >
                                                Selected ({checkedIds.length}{" "}
                                                month
                                                {checkedIds.length > 1
                                                  ? "s"
                                                  : ""}
                                                ) — Total Pending:
                                              </td>
                                              <td className="text-end text-danger fs-6">
                                                {rupee(totalSelectedPending)}
                                              </td>
                                              <td></td>
                                            </tr>
                                          </tfoot>
                                        )}
                                      </table>
                                    </div>

                                    {/* Payment form — appears when months are selected */}
                                    {checkedIds.length > 0 && (
                                      <form onSubmit={handlePay}>
                                        <div className="border rounded-3 bg-light p-3 mb-3">
                                          <div className="fw-semibold mb-1">
                                            <i className="ri-checkbox-multiple-line me-2 text-primary"></i>
                                            Paying for:{" "}
                                            {selectedRows
                                              .map((s) => s.month_label)
                                              .join(", ")}
                                          </div>
                                          <small className="text-muted">
                                            Total pending:{" "}
                                            <strong className="text-danger">
                                              {rupee(totalSelectedPending)}
                                            </strong>
                                          </small>
                                        </div>

                                        <div className="row g-3">
                                          <div className="col-md-6">
                                            <BankSelect />
                                          </div>
                                          <div className="col-md-6">
                                            <div className="form-floating form-floating-outline">
                                              <input
                                                type="date"
                                                className="form-control"
                                                value={payForm.payment_date}
                                                onChange={(e) =>
                                                  setPayForm((p) => ({
                                                    ...p,
                                                    payment_date:
                                                      e.target.value,
                                                  }))
                                                }
                                              />
                                              <label>Payment Date</label>
                                            </div>
                                          </div>

                                          {/* Custom amount toggle */}
                                          <div className="col-12">
                                            <div className="form-check form-switch">
                                              <input
                                                className="form-check-input"
                                                type="checkbox"
                                                id="customToggle"
                                                checked={useCustomAmount}
                                                onChange={(e) => {
                                                  setUseCustomAmount(
                                                    e.target.checked,
                                                  );
                                                  if (!e.target.checked)
                                                    setCustomAmount("");
                                                }}
                                              />
                                              <label
                                                className="form-check-label"
                                                htmlFor="customToggle"
                                              >
                                                Pay a partial / custom amount
                                              </label>
                                            </div>
                                          </div>

                                          <div className="col-md-6">
                                            {useCustomAmount ? (
                                              <div className="form-floating form-floating-outline">
                                                <input
                                                  type="number"
                                                  className="form-control"
                                                  min="1"
                                                  max={totalSelectedPending}
                                                  value={customAmount}
                                                  onChange={(e) =>
                                                    setCustomAmount(
                                                      e.target.value,
                                                    )
                                                  }
                                                  placeholder="Amount"
                                                  required
                                                />
                                                <label>
                                                  Custom Amount (max:{" "}
                                                  {rupee(totalSelectedPending)})
                                                </label>
                                              </div>
                                            ) : (
                                              <div className="border rounded-3 p-3 bg-white">
                                                <small className="text-muted d-block mb-1">
                                                  Amount to Pay
                                                </small>
                                                <h5 className="fw-bold text-success mb-0">
                                                  {rupee(totalSelectedPending)}
                                                </h5>
                                              </div>
                                            )}
                                          </div>

                                          <div className="col-md-6">
                                            <div className="form-floating form-floating-outline">
                                              <input
                                                type="text"
                                                className="form-control"
                                                placeholder="Remark"
                                                value={payForm.remark}
                                                onChange={(e) =>
                                                  setPayForm((p) => ({
                                                    ...p,
                                                    remark: e.target.value,
                                                  }))
                                                }
                                              />
                                              <label>Remark (optional)</label>
                                            </div>
                                          </div>

                                          <div className="col-12">
                                            <div className="d-flex align-items-center justify-content-between bg-label-success rounded-3 p-3">
                                              <div>
                                                <small className="text-muted d-block">
                                                  Final Amount
                                                </small>
                                                <h5 className="fw-bold text-success mb-0">
                                                  {rupee(normalPayAmount)}
                                                </h5>
                                              </div>
                                              <button
                                                type="submit"
                                                className="btn btn-success px-5"
                                                disabled={
                                                  loading ||
                                                  normalPayAmount <= 0
                                                }
                                              >
                                                <i className="ri-check-double-line me-2"></i>
                                                {loading
                                                  ? "Processing..."
                                                  : "Pay Now"}
                                              </button>
                                            </div>
                                          </div>
                                        </div>
                                      </form>
                                    )}
                                  </>
                                )}

                                {/* ── Paid History (collapsed) ────────────── */}
                                {paidHistory.length > 0 && (
                                  <div className="mt-4">
                                    <button
                                      className="btn btn-outline-secondary btn-sm w-100 d-flex align-items-center justify-content-between"
                                      onClick={() =>
                                        setShowPaidHistory(!showPaidHistory)
                                      }
                                    >
                                      <span>
                                        <i className="ri-history-line me-2"></i>
                                        Paid History — {paidHistory.length}{" "}
                                        month{paidHistory.length > 1 ? "s" : ""}
                                        &nbsp;·&nbsp;Total Paid:{" "}
                                        {rupee(summary?.total_paid_amount)}
                                      </span>
                                      <i
                                        className={`ri-arrow-${showPaidHistory ? "up" : "down"}-s-line`}
                                      ></i>
                                    </button>

                                    {showPaidHistory && (
                                      <div className="table-responsive mt-2 border rounded-3">
                                        <table className="table align-middle mb-0 table-sm">
                                          <thead className="table-light">
                                            <tr>
                                              <th>Month</th>
                                              <th className="text-center">
                                                Working Days
                                              </th>
                                              <th className="text-center">
                                                Present Days
                                              </th>
                                              <th className="text-end">
                                                Base Salary
                                              </th>
                                              <th className="text-end">
                                                Net Salary
                                              </th>
                                              <th className="text-end text-success">
                                                Paid
                                              </th>
                                              <th className="text-center">
                                                Status
                                              </th>
                                            </tr>
                                          </thead>
                                          <tbody>
                                            {paidHistory.map((h) => (
                                              <tr key={h.salary_id}>
                                                <td className="fw-semibold">
                                                  {h.month_label}
                                                </td>
                                                <td className="text-center">
                                                  {h.working_days}
                                                </td>
                                                <td className="text-center text-success">
                                                  {h.present_days}
                                                </td>
                                                <td className="text-end">
                                                  {rupee(h.base_salary)}
                                                </td>
                                                <td className="text-end fw-semibold">
                                                  {rupee(h.net_salary)}
                                                </td>
                                                <td className="text-end text-success fw-semibold">
                                                  {rupee(h.paid_amount)}
                                                </td>
                                                <td className="text-center">
                                                  <span className="badge bg-label-success rounded-pill px-3">
                                                    Paid
                                                  </span>
                                                </td>
                                              </tr>
                                            ))}
                                          </tbody>
                                          <tfoot className="table-light fw-bold">
                                            <tr>
                                              <td
                                                colSpan={4}
                                                className="text-end"
                                              >
                                                Total:
                                              </td>
                                              <td className="text-end">
                                                {rupee(
                                                  paidHistory.reduce(
                                                    (a, h) => a + h.net_salary,
                                                    0,
                                                  ),
                                                )}
                                              </td>
                                              <td className="text-end text-success">
                                                {rupee(
                                                  paidHistory.reduce(
                                                    (a, h) => a + h.paid_amount,
                                                    0,
                                                  ),
                                                )}
                                              </td>
                                              <td></td>
                                            </tr>
                                          </tfoot>
                                        </table>
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>
                            )}

                            {/* ── ADVANCE TAB ────────────────────────────────── */}
                            {paymentType === "advance" && (
                              <div className="card-body">
                                <div className="alert alert-warning d-flex gap-2 mb-4">
                                  <i className="ri-information-line fs-5 mt-1 flex-shrink-0"></i>
                                  <div>
                                    <strong>
                                      Advance Salary for {currentMonthLabel}
                                    </strong>
                                    <br />
                                    You are paying salary for the current month
                                    before it ends. On the 1st of next month,
                                    the system will automatically calculate the
                                    salary. The advance paid will be deducted
                                    from the net salary and only the balance
                                    will be pending.
                                    {currentAdvance && (
                                      <div className="mt-2 fw-semibold text-danger">
                                        Already advanced this month:{" "}
                                        {rupee(currentAdvance.advance_given)}
                                      </div>
                                    )}
                                  </div>
                                </div>

                                <form onSubmit={handlePay}>
                                  <div className="row g-3">
                                    <div className="col-md-6">
                                      <div className="form-floating form-floating-outline">
                                        <input
                                          type="number"
                                          className="form-control"
                                          placeholder="0"
                                          min="1"
                                          value={payForm.advance_amount}
                                          onChange={(e) =>
                                            setPayForm((p) => ({
                                              ...p,
                                              advance_amount: e.target.value,
                                            }))
                                          }
                                          required
                                        />
                                        <label>Advance Amount (₹)</label>
                                      </div>
                                    </div>
                                    <div className="col-md-6">
                                      <BankSelect />
                                    </div>
                                    <div className="col-md-6">
                                      <div className="form-floating form-floating-outline">
                                        <input
                                          type="date"
                                          className="form-control"
                                          value={payForm.payment_date}
                                          onChange={(e) =>
                                            setPayForm((p) => ({
                                              ...p,
                                              payment_date: e.target.value,
                                            }))
                                          }
                                        />
                                        <label>Payment Date</label>
                                      </div>
                                    </div>
                                    <div className="col-md-6">
                                      <div className="form-floating form-floating-outline">
                                        <input
                                          type="text"
                                          className="form-control"
                                          placeholder="Remark"
                                          value={payForm.remark}
                                          onChange={(e) =>
                                            setPayForm((p) => ({
                                              ...p,
                                              remark: e.target.value,
                                            }))
                                          }
                                        />
                                        <label>Remark (optional)</label>
                                      </div>
                                    </div>
                                    <div className="col-12">
                                      <div className="d-flex align-items-center justify-content-between bg-label-warning rounded-3 p-3">
                                        <div>
                                          <div className="fw-semibold">
                                            {selectedStaff?.name} —{" "}
                                            {currentMonthLabel}
                                          </div>
                                          <small className="text-muted">
                                            Advance:{" "}
                                            <strong>
                                              {rupee(advancePayAmount)}
                                            </strong>
                                          </small>
                                        </div>
                                        <button
                                          type="submit"
                                          className="btn btn-warning px-4"
                                          disabled={
                                            loading || advancePayAmount <= 0
                                          }
                                        >
                                          <i className="ri-send-plane-line me-2"></i>
                                          {loading
                                            ? "Processing..."
                                            : "Pay Advance"}
                                        </button>
                                      </div>
                                    </div>
                                  </div>
                                </form>
                              </div>
                            )}
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
